import { type AffixRef } from 'antd';
import { type MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { observeResize } from '../../core/dom/observeResize.ts';

export interface IAffixAutoUpdate<T extends HTMLElement = HTMLElement> {
  /** Ref da passare a `<Affix ref={...}>`. */
  affixRef: MutableRefObject<AffixRef | null>;
  /** Ref da agganciare all'elemento contenuto nell'Affix. */
  contentRef: MutableRefObject<T | null>;
}

function getResizeTargets(element: Element): Element[] {
  const { parentElement } = element;
  return element === document.body || !parentElement
    ? [element]
    : [element, ...getResizeTargets(parentElement)];
}

/**
 * Forza il ricalcolo di un `Affix` antd quando la pagina cambia altezza senza
 * eventi scroll/resize.
 */
export function useAffixAutoUpdate<
  T extends HTMLElement = HTMLElement,
>(): IAffixAutoUpdate<T> {
  const affixRef = useRef<AffixRef | null>(null);
  const contentRef = useRef<T | null>(null);

  useEffect(() => {
    if (!contentRef.current) return;
    const observer = observeResize(getResizeTargets(contentRef.current), () =>
      affixRef.current?.updatePosition(),
    );
    return () => observer?.disconnect();
  }, []);

  return useMemo(() => ({ affixRef, contentRef }), []);
}
